import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "actions";
import CallNotStartedDialog from "./CallNotStartedDialog";
import CancelSessionDialog from "../WeeklyCalendarSessions/CancelSessionDialog";
import { getCancellationReasonLabel, getKeyCancellationReason } from "constants/cancelReasons";
import { getClinicianCallsNeverStarted } from "../../selectors";

/**
 * CallsNotStartedHandler
 * @param {Object} props
 * @param {string} props.clinicianId - The clinician's id
 */
export default function CallsNotStartedHandler({ clinicianId }) {
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [reason, setReason] = useState("");
  const [responsibleForCancellation, setResponsibleForCancellation] = useState("");
  const [cancelReasonText, setCancelReasonText] = useState("");
  const [cancelSessionOpen, setCancelSessionOpen] = useState(false);
  const [manageCall, setManageCall] = useState(null);

  const callsNeverStarted = useSelector(getClinicianCallsNeverStarted);

  useEffect(() => {
    if (clinicianId) {
      dispatch(actions.getClinicianCallsNeverStarted(clinicianId));
    }
  }, [clinicianId, dispatch]);

  // Track previous calls length to only open dialog when length changes and is > 0
  const prevCallsLength = useRef(0);
  useEffect(() => {
    const currentLength = callsNeverStarted?.length ?? 0;
    if (prevCallsLength.current !== currentLength && currentLength > 0) {
      setDetails(callsNeverStarted[0]);
      setReason("");
      setCancelReasonText("");
      setResponsibleForCancellation("");
      setDialogOpen(true);
    }
    prevCallsLength.current = currentLength;
  }, [callsNeverStarted]);

  // Refetch calls when both dialogs are closed
  const prevDialogOpen = useRef(false);
  const prevCancelOpen = useRef(false);
  useEffect(() => {
    if ((prevDialogOpen.current || prevCancelOpen.current) && !dialogOpen && !cancelSessionOpen) {
      dispatch(actions.getClinicianCallsNeverStarted(clinicianId));
    }
    prevDialogOpen.current = dialogOpen;
    prevCancelOpen.current = cancelSessionOpen;
  }, [dialogOpen, cancelSessionOpen, dispatch, clinicianId]);

  const handleClose = () => {
    setDialogOpen(false);
    setDetails(null);
    setReason("");
  };

  const handleSubmit = () => {
    if (!details || !reason) return;
    if (reason === "cancel") {
      setManageCall(details);
      setCancelSessionOpen(true);
      setDialogOpen(false);
    } else if (reason === "no-show") {
      dispatch(actions.markClientNoShow(details.id));
      dispatch(actions.getClinicianCallsNeverStarted(clinicianId));
      handleClose();
    }
  };

  const onChangeResponsibleForCancellation = (e) => {
    setResponsibleForCancellation(e.target.value);
    setCancelReasonText(getCancellationReasonLabel(e.target.value));
  };

  const submitCancelSession = () => {
    dispatch(
      actions.cancelVideoCall({
        message: cancelReasonText,
        responsibleForCancellation: getKeyCancellationReason(responsibleForCancellation),
        id: manageCall.id,
        clinicianUserId: manageCall.clinician_user_id,
        clientId: manageCall.client_id,
        cancelAllInstances: false,
      })
    );
    dispatch(actions.getClinicianCallsNeverStarted(clinicianId));
    setCancelSessionOpen(false);
  };

  if (!details) return null;
  return (
    <>
      <CallNotStartedDialog
        sessionDetails={details}
        open={dialogOpen}
        handleClose={handleClose}
        callNotStartedReason={reason}
        onChangeCallNotStartedReason={(e) => setReason(e.target.value)}
        onSubmitCallNotStartedReason={handleSubmit}
      />
      {cancelSessionOpen && manageCall && (
        <CancelSessionDialog
          sessionDetails={manageCall}
          open={cancelSessionOpen}
          closeDialog={() => {
            setDialogOpen(true);
            setCancelSessionOpen(false);
          }}
          onChangeCancelReason={(e) => setCancelReasonText(e.target.value)}
          submitCancelSession={submitCancelSession}
          cancelReasonText={cancelReasonText}
          responsibleForCancellation={responsibleForCancellation}
          onChangeResponsibleForCancellation={onChangeResponsibleForCancellation}
          disableClose={true}
          hideAlert={true}
        />
      )}
    </>
  );
}
